import React from "react";
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import CheckIcon from "@mui/icons-material/Check";
import MapIcon from "@mui/icons-material/Map";
import { PLAN_QUOTAS } from "@routebot/constants";
import { useTranslation } from "react-i18next";

const PlanCard = ({ plan, isCurrent, isUpgrade, isDowngrade, onChangePlan, isSubmitting, isSubmittingGlobal }) => {
  const { t } = useTranslation();

  const isPlanRecommended = isUpgrade && plan?.recommended;

  // Get monthly map quota for this plan (all quota types have same value, so take first one)
  const planQuotas = PLAN_QUOTAS[plan?.key] || {};
  const monthlyMapQuota = Object.values(planQuotas)[0] || 0;
  const formattedQuota = monthlyMapQuota >= 1000 ? `${(monthlyMapQuota / 1000).toLocaleString()}K` : monthlyMapQuota;

  return (
    <Card
      sx={{
        height: "100%",
        borderWidth: isPlanRecommended ? 2 : 1,
        borderStyle: "solid",
        borderColor: isPlanRecommended ? "warning.main" : "divider",
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" mb={1}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {plan.name}
          </Typography>

          {isPlanRecommended && <Chip label={t("subscription.recommendedBadge")} size="small" color="warning" />}
          {isCurrent && <Chip label={t("subscription.currentPlan")} size="small" color="primary" />}
        </Box>

        <Typography variant="h4" component="div" gutterBottom>
          {plan.priceMonthly} <span style={{ fontSize: "14px" }}>$/month</span>
          {plan?.pricePerRoute > 0 && (
            <span style={{ fontSize: "14px", color: "#6b7280", fontWeight: 400, marginLeft: "8px" }}>
              {t("subscription.pricePerRoute", { price: plan.pricePerRoute })}
            </span>
          )}
        </Typography>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          {t(plan?.description)}
        </Typography>

        <List dense>
          {(plan?.features ?? []).map((feature) => (
            <ListItem key={feature} disableGutters>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <CheckIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t(feature, { count: plan?.maxRoutes })}
              />
            </ListItem>
          ))}
          {monthlyMapQuota > 0 && (
            <ListItem disableGutters>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <MapIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t("features.monthlyMapQuota", { count: formattedQuota })} />
            </ListItem>
          )}
        </List>
      </CardContent>

      <CardActions sx={{ p: 2, pt: 0 }}>
        {isCurrent ? (
          <Button fullWidth disabled variant="outlined" sx={{ textTransform: "none" }}>
            {t("subscription.currentPlan")}
          </Button>
        ) : (
          <Button
            fullWidth
            sx={{ textTransform: "none" }}
            variant={isUpgrade ? "contained" : "outlined"}
            color={isUpgrade ? "primary" : "inherit"}
            onClick={() => onChangePlan(plan.key)}
            disabled={isSubmittingGlobal}
            startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isUpgrade
              ? t("subscription.ctaUpgrade")
              : isDowngrade
                ? t("subscription.ctaDowngrade")
                : t("subscription.ctaSelect")}
          </Button>
        )}
      </CardActions>
    </Card>
  );
};

export default PlanCard;
